/**
 * XLM - Innovation & Technology | João Rodrigues | v2.0 | 2026*
 * 
 * Substituição de localStorage por IndexedDB:
 * - Maior capacidade (centenas de MB vs 5-10MB)
 * - Operações assíncronas (não bloqueia UI)
 * - Suporte para armazenamento de ficheiros binários
 */

import { isSavedUserFile, SavedUserFile, UserFile } from "../types/UserFile";

const DB_NAME = "AnonymizerDB";
const STORE_NAME = "files";
const DELETED_STORE = "deleted_files";
const DB_VERSION = 1;

let dbCache :IDBDatabase | null = null;

function openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = () => {
            const db = request.result;
            let store;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                store = db.createObjectStore(STORE_NAME, { keyPath: "name" });    
            }
            else{
                store = request.transaction!.objectStore(STORE_NAME);
            }

            if(!store.indexNames.contains("meta")){
                store.createIndex("meta", ["name", "imported", "modified"], { unique: false });
            }
            
            if(!db.objectStoreNames.contains(DELETED_STORE)) {
                db.createObjectStore(DELETED_STORE, { keyPath: "name" });
            }
        };

    
        request.onsuccess = () => {
            dbCache = request.result;
            dbCache.onversionchange = () => {
                dbCache?.close();
                dbCache = null;
            };
            resolve(dbCache);
        };

        request.onerror = () => {
            if (process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'qa') {
                console.error("IndexedDB: erro ao abrir", request.error);
            }
            reject(request.error);
        };
    });
}

function alertUpdateListUserFile() {
    window.dispatchEvent(new CustomEvent("AlertUpdateListUserFile"));
}

async function getReadyDB(): Promise<IDBDatabase>{
    if(dbCache) return dbCache;
    return await openDB();
}

export async function createUserFile(userFile: SavedUserFile): Promise<boolean> {
    try {
        const db = await getReadyDB();
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);
        
        const size= new Blob([JSON.stringify(userFile)]).size;
        (userFile as any).fileSize = size;
        
        store.put(userFile);

        return new Promise((resolve) => {
            tx.oncomplete = () => {
                alertUpdateListUserFile();
                resolve(true);
            };
            tx.onerror = () => {
                if (process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'qa') {
                    console.error("IndexedDB: erro ao guardar " + userFile.name, tx.error);
                }
                resolve(false);
            };
        });
    } catch (e) {
        if (process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'qa') {
            console.error("IndexedDB: exceção ao guardar " + userFile.name, e);
        }
        return false;
    }
}

export async function readSavedUserFile(name: string): Promise<SavedUserFile | undefined> {
    try {
        const db = await getReadyDB();
        const tx = db.transaction(STORE_NAME, "readonly");
        const store = tx.objectStore(STORE_NAME);
        const request = store.get(name);

        return new Promise<SavedUserFile | undefined>((resolve) => {
            request.onsuccess = () => {
                const res = request.result;
                if (res && isSavedUserFile(res)) {
                    resolve(res);
                } else {
                    resolve(undefined);
                }
            };
            request.onerror = () => {
                if (process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'qa') {
                    console.error("IndexedDB: erro ao ler " + name, request.error);
                }
                resolve(undefined);
            };
        });
    } catch (e) {
        if (process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'qa') {
            console.error("IndexedDB: exceção ao ler " + name, e);
        }
        return undefined;
    }
}

export async function updateUserFile(userFile: SavedUserFile): Promise<boolean> {
    return createUserFile(userFile);
}

export async function deleteUserFile(userFile: SavedUserFile): Promise<void> {
    try {
        const db = await getReadyDB();
        
        const entCount = {} as Record<string, number>;
        const entities = (userFile instanceof UserFile) ? userFile.pool.entities : userFile.ents;
        
        entities.forEach(e => {
            entCount[e.type] = (entCount[e.type] || 0) + 1;
        });

        const deletedData = {
            name: userFile.name,
            imported: userFile.imported,
            modified: userFile.modified,
            entCount: entCount,
            deletedAt: new Date().toISOString()
        };

        const tx = db.transaction([STORE_NAME, DELETED_STORE], "readwrite");
        
        tx.objectStore(DELETED_STORE).put(deletedData);
        tx.objectStore(STORE_NAME).delete(userFile.name);

        return new Promise((resolve,reject) => {
            tx.oncomplete = () => {
                alertUpdateListUserFile();
                resolve();
            };
            tx.onerror = () => {
                if (process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'qa') {
                    console.error("IndexedDB: erro ao eliminar " + userFile.name, tx.error);
                }
                reject(tx.error);
            };
        });
    } catch (e) {
        if (process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'qa') {
            console.error("IndexedDB: exceção ao eliminar " + userFile.name, e);
        }
    }
}

export async function listUserFile(): Promise<SavedUserFile[]> {
    try {
        const db = await getReadyDB();
        const tx = db.transaction(STORE_NAME, "readonly");
        const store = tx.objectStore(STORE_NAME);
        const request = store.getAll();

        return new Promise((resolve) => {
            request.onsuccess = () => {
                const list = request.result as SavedUserFile[];
                resolve(list.filter(item => isSavedUserFile(item)));
            };
            request.onerror = () => {
                if (process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'qa') {
                    console.error("IndexedDB: erro ao listar", request.error);
                }
                resolve([]);
            };
        });
    } catch (e) {
        if (process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'qa') {
            console.error("IndexedDB: exceção ao listar", e);
        }
        return [];
    }
}

export async function clearAllUserFiles(): Promise<void> {
    try {
        const db = await getReadyDB();
        const tx = db.transaction([STORE_NAME], "readwrite");
        tx.objectStore(STORE_NAME).clear();
        
        return new Promise((resolve) => {
            tx.oncomplete = () => {
                alertUpdateListUserFile();
                resolve();
            };
            tx.onerror = () => {
                if (process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'qa') {
                    console.error("IndexedDB: erro ao limpar", tx.error);
                }
            };
        });
    } catch (e) {
        if (process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'qa') {
            console.error("IndexedDB: exceção ao limpar", e);
        }
    }
}


